import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Home() {
  const { t } = useTranslation();

  const featuredArticles = articles.slice(0, 6);

  const stats = [
    { icon: 'ri-user-star-line', value: '2,800万+', label: t('stats.members') },
    { icon: 'ri-article-line', value: '15,000+', label: t('stats.articles') },
    { icon: 'ri-stack-line', value: '8', label: t('stats.categories') },
    { icon: 'ri-medal-line', value: '500+', label: t('stats.coaches') },
  ];

  const seoData = {
    title: '开元体育官方 - 专业体育运动平台 | 赛事资讯、运动健身、体育娱乐',
    description: '开元体育是您首选的专业体育运动平台，提供丰富的体育赛事资讯、运动健身指导及体育娱乐服务。立即加入开元体育，体验顶级运动生态，享受专业体育内容与互动社区。',
    keywords: '开元体育,开元体育官网,开元体育平台,开元体育注册,开元体育登录,开元体育赛事,开元体育会员,体育平台,运动健身,赛事资讯',
    canonicalUrl: 'https://www.kaiyuansports.com/',
    schemaJson: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '开元体育官方首页',
      description: '专业体育运动平台，提供体育赛事资讯、运动健身指导及体育娱乐服务',
      url: 'https://www.kaiyuansports.com/',
      isPartOf: {
        '@type': 'WebSite',
        name: '开元体育',
        url: 'https://www.kaiyuansports.com/',
      },
    },
  };

  return (
    <>
      <SEOHead {...seoData} />

      <section className="relative w-full h-[480px] md:h-[620px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Dynamic%20sports%20collage%20with%20athletes%20in%20motion%2C%20runner%20sprinter%2C%20soccer%20player%20kicking%20ball%2C%20basketball%20player%20dunking%2C%20swimmer%20in%20water%2C%20all%20in%20silhouette%20style%20against%20warm%20sunset%20gradient%20background%2C%20energetic%20atmosphere%2C%20cinematic%20lighting%2C%20dramatic%20contrast%2C%20professional%20sports%20photography%20style&width=1920&height=1080&seq=hero-kaiyuan-sports&orientation=landscape"
            alt="开元体育 - 专业运动平台"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>

        <div className="relative z-10 w-full px-4 md:px-6 lg:px-8 text-center max-w-4xl mx-auto">
          <p className="text-accent-300 text-sm md:text-base font-medium tracking-widest uppercase mb-3 animate-[fadeIn_0.6s_ease-out]">
            {t('hero.subtitle')}
          </p>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white font-heading mb-5 tracking-tight animate-[fadeIn_0.8s_ease-out]">
            开元体育 · <span className="text-accent-300">{t('hero.title')}</span>
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed mb-8 animate-[fadeIn_1s_ease-out]">
            {t('hero.description')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-[fadeIn_1.2s_ease-out]">
            <Link
              to="/about#contact"
              className="px-8 py-3 text-sm font-semibold text-foreground-950 bg-accent-400 hover:bg-accent-300 rounded-full transition-all duration-300 whitespace-nowrap"
            >
              {t('hero.cta')}
            </Link>
            <Link
              to="/category/football"
              className="px-8 py-3 text-sm font-medium text-white border border-white/40 hover:border-white/80 hover:bg-white/10 rounded-full transition-all duration-300 whitespace-nowrap"
            >
              {t('hero.learnmore')}
            </Link>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
              {t('categories.title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
              {t('categories.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="group bg-background-50 border border-background-200/80 rounded-xl p-5 md:p-6 hover:border-accent-300/60 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center bg-accent-100 rounded-xl mb-4 group-hover:bg-accent-200 transition-colors duration-300">
                  <i className={`${cat.icon} text-xl md:text-2xl text-accent-600`}></i>
                </div>
                <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-1 whitespace-nowrap">
                  {cat.name}
                </h3>
                <p className="text-xs text-foreground-400">
                  {cat.articleCount} 篇文章
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
              {t('featured.title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-500">
              {t('featured.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {featuredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group bg-background-50 border border-background-200/80 rounded-xl overflow-hidden hover:border-accent-300/60 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="relative h-44 md:h-48 overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-0.5 text-xs font-medium bg-accent-500 text-background-50 rounded-full">
                      {article.categoryName}
                    </span>
                  </div>
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                    {article.title}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-500 line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.publishedAt}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {article.views.toLocaleString()}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="relative w-full px-4 md:px-6 lg:px-8 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center bg-accent-100/80 rounded-2xl mb-4">
                  <i className={`${stat.icon} text-2xl text-accent-600`}></i>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-1">
                  {stat.value}
                </div>
                <p className="text-xs md:text-sm text-foreground-500">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20 bg-background-100">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-4">
            {t('cta.title')}
          </h2>
          <p className="text-sm md:text-base text-foreground-500 mb-8 leading-relaxed">
            {t('cta.subtitle')}
          </p>
          <Link
            to="/about#contact"
            className="inline-block px-8 py-3.5 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-full transition-all duration-300 whitespace-nowrap"
          >
            {t('cta.button')}
          </Link>
        </div>
      </section>
    </>
  );
}